import type { TypedWrappedFieldProps } from "redux-form";

import { SkillIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useFilterPopover } from "@js/hooks/use-filter-popover";
import type { Job, JobSkill } from "@js/types/jobs";

import { BidListFilterPopover } from "../../components/bid-list-filter-popover";
import { useSelectedJobSkills } from "../../hooks/use-selected-job-skills";

import type { BidSkillsFilterFormValues } from "./bid-skills-filter-form";
import { BidSkillsFilterForm } from "./bid-skills-filter-form";

type BidSkillsFilterProps = {
  job: Pick<Job, "job_skills">;
  skills: TypedWrappedFieldProps<number[] | "" | undefined>;
  top_skills: TypedWrappedFieldProps<number[] | "" | undefined>;
};

export const BidSkillsFilter = ({
  job,
  skills: skillsField,
  top_skills,
}: BidSkillsFilterProps) => {
  const { anchorElement, isOpen, handleClick, handleClose } =
    useFilterPopover();
  const { skills, topSkills } = useSelectedJobSkills({
    skills: skillsField.input.value,
    topSkills: top_skills.input.value,
    jobSkills: job.job_skills,
  });
  const isFilterApplied = !!skills.length || !!topSkills.length;

  const handleApply = (values: BidSkillsFilterFormValues) => {
    skillsField.input.onChange(values.skills);
    top_skills.input.onChange(values.top_skills);
    handleClose();
  };

  return (
    <BidListFilterPopover
      label={getBidSkillsFilterLabel({
        topSkills,
        skills,
        jobSkills: job.job_skills,
      })}
      icon={<SkillIcon />}
      isFilterApplied={isFilterApplied}
      isFilterOpen={isOpen}
      onClick={handleClick}
      onClose={handleClose}
      anchorElement={anchorElement}
    >
      <BidSkillsFilterForm
        onApply={handleApply}
        job={job}
        currentSkills={skills}
        currentTopSkills={topSkills}
      />
    </BidListFilterPopover>
  );
};

export type GetBidSkillsFilterLabelArg = {
  topSkills: number[];
  skills: number[];
  jobSkills: JobSkill[];
};

export const getBidSkillsFilterLabel = ({
  topSkills,
  skills,
  jobSkills,
}: GetBidSkillsFilterLabelArg): string => {
  const selectedSkills = [...topSkills, ...skills];
  const selectedSkillsCount = selectedSkills.length;
  if (!selectedSkillsCount) {
    return "Skills";
  }

  const otherSkillsCount = selectedSkillsCount - 1;
  const suffix = otherSkillsCount ? ` & ${otherSkillsCount} other` : "";
  const firstSelectedSkill = jobSkills.find(
    (jobSkill) => jobSkill.skill.id === selectedSkills[0],
  );
  if (!firstSelectedSkill) {
    return "Skills";
  }

  return `${firstSelectedSkill.skill.name}${suffix}`;
};
